import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageMeta } from "@/components/Layout";
import { Reveal } from "@/components/Reveal";
import { Mandala } from "@/components/Mandala";
import { ImageSlot } from "@/components/ImageSlot";
import { SectionHeader } from "@/components/Section";
import { Ambient } from "@/components/Ambient";
import { HOME, SEO, IMAGES, MAHAVIDYAS } from "@/content/siteContent";

/* Drifting golden particles for the hero */
const PARTICLES = [
    { left: "12%", top: "68%", size: 5, time: "15s", delay: "0s", x: "26px", y: "-130px", o: 0.5 },
    { left: "22%", top: "82%", size: 4, time: "18s", delay: "2s", x: "-20px", y: "-150px", o: 0.4 },
    { left: "38%", top: "74%", size: 6, time: "13s", delay: "4s", x: "18px", y: "-110px", o: 0.45 },
    { left: "55%", top: "80%", size: 4, time: "17s", delay: "1s", x: "-24px", y: "-140px", o: 0.35 },
    { left: "68%", top: "70%", size: 5, time: "14s", delay: "3s", x: "22px", y: "-120px", o: 0.5 },
    { left: "80%", top: "84%", size: 4, time: "19s", delay: "5s", x: "-16px", y: "-160px", o: 0.4 },
    { left: "90%", top: "66%", size: 6, time: "16s", delay: "2.5s", x: "-28px", y: "-100px", o: 0.45 },
    { left: "6%", top: "88%", size: 4, time: "20s", delay: "6s", x: "20px", y: "-170px", o: 0.35 },
];

const Hero = () => (
    <section
        className="relative flex min-h-[100svh] items-end overflow-hidden bg-[#10080B]"
        data-testid="hero-section"
        aria-label="Welcome to Dasamahavidya Puja"
    >
        {/* Atmosphere */}
        <div
            className="absolute inset-0"
            style={{
                background:
                    "radial-gradient(ellipse 90% 70% at 68% 38%, rgba(38,9,16,0.4) 0%, #10080B 78%)",
            }}
            aria-hidden="true"
        />
        <div
            className="glow-breathe absolute left-1/2 top-[38%] h-[60vmin] w-[60vmin] -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{
                background:
                    "radial-gradient(circle, rgba(200,161,90,0.14) 0%, rgba(200,161,90,0) 65%)",
            }}
            aria-hidden="true"
        />

        {/* Ornamental mandala rings */}
        <Mandala className="absolute left-1/2 top-[40%] h-[135vmin] w-[135vmin] -translate-x-1/2 -translate-y-1/2 opacity-60" />

        {/* Supplied altar photograph — cinematic reveal, then perpetual slow zoom */}
        <div className="hero-media absolute inset-0">
            <img
                src={IMAGES.hero}
                alt="The Dasamahavidya altar at Chetla — the ten Mahavidyas adorned with red hibiscus garlands during the night of worship"
                className="h-[116%] w-full object-cover object-[70%_top] sm:h-full sm:object-[62%_center]"
                fetchpriority="high"
                data-testid="hero-image"
            />
        </div>
        {/* Legibility overlay — keeps the idol visible while anchoring the text */}
        <div
            className="absolute inset-0"
            style={{
                background:
                    "linear-gradient(to top, rgba(16,8,11,0.92) 0%, rgba(16,8,11,0.45) 34%, rgba(16,8,11,0.08) 60%, rgba(16,8,11,0.35) 100%), linear-gradient(100deg, rgba(16,8,11,0.85) 0%, rgba(16,8,11,0.5) 36%, rgba(16,8,11,0) 66%)",
            }}
            aria-hidden="true"
        />

        {/* Particles */}
        {PARTICLES.map((p, i) => (
            <span
                key={i}
                className="particle"
                aria-hidden="true"
                style={{
                    left: p.left,
                    top: p.top,
                    width: p.size,
                    height: p.size,
                    "--p-time": p.time,
                    "--p-delay": p.delay,
                    "--p-x": p.x,
                    "--p-y": p.y,
                    "--p-opacity": p.o,
                }}
            />
        ))}

        {/* Typography */}
        <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-28 pt-40 sm:px-8 sm:pb-32">
            <p className="eyebrow rise" style={{ "--rise-delay": "1.7s" }} data-testid="hero-eyebrow">
                {HOME.heroEyebrow}
            </p>
            <h1 className="font-display mt-6 max-w-3xl text-4xl font-semibold leading-[1.06] text-[#FFF5E6] [text-shadow:0_2px_24px_rgba(16,8,11,0.6)] sm:text-6xl lg:text-6xl">
                <span className="rise block" style={{ "--rise-delay": "2.1s" }} data-testid="hero-headline-1">
                    {HOME.heroLine1}
                </span>
                <span className="rise mt-2 block text-[#C8A15A]" style={{ "--rise-delay": "2.5s" }} data-testid="hero-headline-2">
                    {HOME.heroLine2}
                </span>
            </h1>
            <p
                className="rise mt-7 max-w-xl text-base font-light italic leading-relaxed text-[#FFF5E6]/85 sm:text-lg"
                style={{ "--rise-delay": "2.9s" }}
                data-testid="hero-tagline"
            >
                {HOME.heroTagline}
            </p>
            <div className="rise mt-10 flex flex-wrap gap-4" style={{ "--rise-delay": "3.3s" }}>
                <Link to="/mahavidyas" className="btn btn-gold" data-testid="hero-cta-mahavidyas">
                    Discover the Mahavidyas
                </Link>
                <Link to="/culture-community" className="btn btn-outline" data-testid="hero-cta-culture">
                    Culture & Community
                </Link>
            </div>
        </div>

        {/* Scroll indicator */}
        <div
            className="rise absolute bottom-7 left-1/2 z-10 -translate-x-1/2"
            style={{ "--rise-delay": "3.8s" }}
            aria-hidden="true"
        >
            <div className="flex h-12 w-7 items-start justify-center rounded-full border border-[#C8A15A]/45 pt-2">
                <span className="scroll-hint-dot block h-2 w-[3px] rounded-full bg-[#C8A15A]" />
            </div>
        </div>
    </section>
);

const Legacy = () => (
    <section className="surface-ivory py-20 sm:py-28" data-testid="legacy-section">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-[1.15fr_1fr] lg:gap-20">
            <div>
                <SectionHeader eyebrow="Our Legacy" title={HOME.legacy.title} />
                <Reveal delay={0.1}>
                    <div className="body-copy mt-7 max-w-xl space-y-5">
                        {HOME.welcome.map((p, i) => (
                            <p key={i}>{p}</p>
                        ))}
                        <p>{HOME.legacy.body[0]}</p>
                    </div>
                </Reveal>
            </div>
            <div className="flex flex-col justify-center gap-10">
                {[
                    { n: "112", label: "Years of Shyama Puja" },
                    { n: "48", label: "Years of Dasamahavidya Puja" },
                ].map((f, i) => (
                    <Reveal key={f.n} delay={0.15 + i * 0.12}>
                        <div className="flex items-baseline gap-6 border-l-2 border-[#A51C30]/60 pl-7">
                            <span
                                className="font-display text-7xl font-semibold leading-none text-[#A51C30] sm:text-8xl"
                                data-testid={`legacy-figure-${f.n}`}
                            >
                                {f.n}
                            </span>
                            <span className="max-w-[10rem] text-xs uppercase tracking-[0.22em] text-[#2b1418]/70">
                                {f.label}
                            </span>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

const Sequence = () => (
    <section className="surface-dark relative overflow-hidden py-20 sm:py-28" data-testid="sequence-section">
        <Ambient />
        <Mandala className="absolute -left-52 top-1/2 h-[560px] w-[560px] -translate-y-1/2 opacity-25" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
            <SectionHeader eyebrow="The Observance" title={HOME.sequence.title} dark />
            <Reveal delay={0.1}>
                <p className="body-copy mt-6 max-w-2xl text-[#FFF5E6]/75">{HOME.sequence.intro}</p>
            </Reveal>
            <div className="mt-14 grid gap-px overflow-hidden rounded-sm bg-[#C8A15A]/15 sm:grid-cols-2 lg:grid-cols-4">
                {HOME.sequence.steps.map((s, i) => (
                    <Reveal key={s.title} delay={0.1 + i * 0.1} className="bg-[#160a0e]">
                        <div className="flex h-full flex-col p-8">
                            <span className="font-display text-4xl font-medium text-[#C8A15A]/70">
                                {String(i + 1).padStart(2, "0")}
                            </span>
                            <h3 className="font-display mt-5 text-2xl font-semibold text-[#FFF5E6]">
                                {s.title}
                            </h3>
                            <p className="mt-3 text-sm font-light leading-relaxed text-[#FFF5E6]/65">
                                {s.body}
                            </p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

const VidyaIntro = () => (
    <section className="surface-maroon relative overflow-hidden py-20 sm:py-28" data-testid="vidya-intro-section">
        <Ambient />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
            <SectionHeader eyebrow="The Divine Mother" title={HOME.vidyaIntro.title} dark />
            <Reveal delay={0.1}>
                <p className="body-copy mt-6 max-w-2xl text-[#FFF5E6]/75">{HOME.vidyaIntro.body}</p>
            </Reveal>
            <div className="mt-14 grid gap-x-10 gap-y-6 sm:grid-cols-2 lg:grid-cols-2">
                {MAHAVIDYAS.map((v, i) => (
                    <Reveal key={v.num} delay={Math.min(i * 0.05, 0.4)}>
                        <Link
                            to="/mahavidyas"
                            className="vidya-card group flex items-baseline gap-5 border border-[#C8A15A]/15 px-6 py-5"
                            data-testid={`home-vidya-link-${v.num}`}
                        >
                            <span className="vidya-num font-display text-2xl text-[#C8A15A]/60">
                                {v.num}
                            </span>
                            <span>
                                <span className="font-display block text-xl font-semibold text-[#FFF5E6]">
                                    {v.name}
                                </span>
                                <span className="mt-1 block text-xs font-light italic tracking-wide text-[#FFF5E6]/55">
                                    {v.tagline}
                                </span>
                            </span>
                        </Link>
                    </Reveal>
                ))}
            </div>
            <Reveal delay={0.2} className="mt-12">
                <Link to="/mahavidyas" className="btn btn-gold" data-testid="vidya-intro-cta">
                    Meet the Ten Mahavidyas <ArrowRight size={15} />
                </Link>
            </Reveal>
        </div>
    </section>
);

const Highlights = () => (
    <section className="surface-ivory py-20 sm:py-28" data-testid="highlights-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <SectionHeader eyebrow="Culture & Kumari Puja" title={HOME.highlights.title} />
            <div className="mt-12 grid gap-12 lg:grid-cols-2 lg:gap-16">
                <Reveal delay={0.1}>
                    <div className="body-copy max-w-xl space-y-5">
                        {HOME.highlights.body.map((p, i) => (
                            <p key={i}>{p}</p>
                        ))}
                        <Link
                            to="/culture-community"
                            className="btn btn-outline--dark mt-4"
                            data-testid="highlights-cta"
                        >
                            Culture & Community <ArrowRight size={15} />
                        </Link>
                    </div>
                </Reveal>
                <div className="grid gap-6">
                    <Reveal delay={0.15}>
                        <ImageSlot slotKey="cultural" aspect="16 / 9" light />
                    </Reveal>
                    <Reveal delay={0.25}>
                        <ImageSlot slotKey="kumariPuja" aspect="16 / 9" light />
                    </Reveal>
                </div>
            </div>
        </div>
    </section>
);

const Community = () => (
    <section className="surface-dark relative overflow-hidden py-20 sm:py-28" data-testid="community-section">
        <Ambient />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
            <div className="grid items-start gap-12 lg:grid-cols-2 lg:gap-16">
                <div>
                    <SectionHeader eyebrow="Community & Agnikar" title={HOME.community.title} dark />
                    <Reveal delay={0.1}>
                        <div className="body-copy mt-7 max-w-xl space-y-5 text-[#FFF5E6]/75">
                            {HOME.community.body.map((p, i) => (
                                <p key={i}>{p}</p>
                            ))}
                        </div>
                    </Reveal>
                </div>
                <div className="grid gap-6">
                    <Reveal delay={0.15}>
                        <ImageSlot slotKey="community" aspect="16 / 9" />
                    </Reveal>
                    <Reveal delay={0.25}>
                        <ImageSlot slotKey="agnikar" aspect="16 / 9" />
                    </Reveal>
                </div>
            </div>
        </div>
    </section>
);

const ClosingBand = () => (
    <section className="surface-ivory py-20 sm:py-24" data-testid="closing-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <div className="grid gap-12 lg:grid-cols-[1.2fr_1fr] lg:gap-20">
                <Reveal>
                    <p className="eyebrow eyebrow--dark">Partnerships</p>
                    <h2 className="font-display mt-5 text-4xl font-semibold leading-tight text-[#260910] sm:text-5xl">
                        {HOME.partnershipInvite.title}
                    </h2>
                    <p className="body-copy mt-6 max-w-xl">{HOME.partnershipInvite.body}</p>
                    <Link to="/partnerships" className="btn btn-outline--dark mt-8" data-testid="closing-partnership-cta">
                        Partnership opportunities <ArrowRight size={15} />
                    </Link>
                </Reveal>
                <div className="flex flex-col justify-center gap-5">
                    <Reveal delay={0.12}>
                        <Link
                            to="/media-features"
                            className="group flex items-center justify-between border border-[#260910]/15 px-7 py-6 transition-colors hover:border-[#A51C30]/50"
                            data-testid="closing-media-link"
                        >
                            <span>
                                <span className="font-display block text-2xl font-semibold text-[#260910]">
                                    Media Features
                                </span>
                                <span className="mt-1 block text-sm font-light text-[#2b1418]/60">
                                    Verified coverage and press enquiries
                                </span>
                            </span>
                            <ArrowRight size={18} className="text-[#A51C30] transition-transform group-hover:translate-x-1" />
                        </Link>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <Link
                            to="/contact"
                            className="group flex items-center justify-between border border-[#260910]/15 px-7 py-6 transition-colors hover:border-[#A51C30]/50"
                            data-testid="closing-contact-link"
                        >
                            <span>
                                <span className="font-display block text-2xl font-semibold text-[#260910]">
                                    Contact Us
                                </span>
                                <span className="mt-1 block text-sm font-light text-[#2b1418]/60">
                                    Reach the organising committee
                                </span>
                            </span>
                            <ArrowRight size={18} className="text-[#A51C30] transition-transform group-hover:translate-x-1" />
                        </Link>
                    </Reveal>
                </div>
            </div>
        </div>
    </section>
);

export default function Home() {
    return (
        <>
            <PageMeta title={SEO.home.title} description={SEO.home.description} />
            <Hero />
            <Legacy />
            <Sequence />
            <VidyaIntro />
            <Highlights />
            <Community />
            <ClosingBand />
        </>
    );
}
